"use client"

import { useState, useEffect } from "react"
import { TrendingUp, TrendingDown, IndianRupee, Calendar, Eye } from "lucide-react"
import { getEarningsDashboard } from '../utils/api'

export default function EarningDashboard() {
    const [data, setData] = useState({
        totalEarnings: 0,
        monthlyChange: 0,
        weeklyEarnings: 0,
        dailyAverage: 0,
        recentActivity: []
    });
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getEarningsDashboard()
            .then(d => setData(d))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, []);

    const {
        totalEarnings,
        monthlyChange,
        weeklyEarnings,
        dailyAverage,
        recentActivity
    } = data;

    const isPositiveChange = monthlyChange > 0;

    const formatCurrency = (amount) =>
        new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        }).format(amount);

    if (loading) {
        return <div className="text-center p-8">Loading…</div>;
    }

    return (
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
            <div className="mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">Earnings Dashboard</h1>
                    <p className="text-slate-600">Track your financial performance</p>
                </div>

                {/* Main Earnings Card */}
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 border border-slate-200">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="p-3 bg-emerald-100 rounded-xl">
                                <IndianRupee className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-700">Total Earnings</h2>
                                <p className="text-sm text-slate-500">All time revenue</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-1">
                            <Eye className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-500">Live</span>
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="text-4xl md:text-5xl font-bold text-slate-800 mb-2">
                            {formatCurrency(totalEarnings)}
                        </div>
                        <div className="flex items-center space-x-2 mb-6">
                            {isPositiveChange
                                ? <TrendingUp className="w-5 h-5 text-emerald-500" />
                                : <TrendingDown className="w-5 h-5 text-red-500" />
                            }
                            <span className={`text-sm font-medium ${isPositiveChange ? "text-emerald-600" : "text-red-600"}`}>
                                {isPositiveChange ? "+" : ""}
                                {monthlyChange}% from last month
                            </span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
                        <div
                            className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: "75%" }}
                        ></div>
                    </div>
                    <p className="text-sm text-slate-500">75% of monthly goal achieved</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {/* Weekly Earnings */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Calendar className="w-5 h-5 text-blue-600" />
                            </div>
                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">This Week</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800 mb-1">{formatCurrency(weeklyEarnings)}</div>
                        <p className="text-sm text-slate-500">Weekly earnings</p>
                    </div>

                    {/* Daily Average */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-purple-600" />
                            </div>
                            <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">Daily Avg</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800 mb-1">{formatCurrency(dailyAverage)}</div>
                        <p className="text-sm text-slate-500">Average per day</p>
                    </div>

                    {/* Growth Rate */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow md:col-span-2 lg:col-span-1">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-orange-600" />
                            </div>
                            <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full">Growth</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800 mb-1">+{monthlyChange}%</div>
                        <p className="text-sm text-slate-500">Monthly growth rate</p>
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Recent Activity</h3>
                    <div className="space-y-4">
                        {(data.recentActivity || []).slice(0, 3).map((item, index) => (
                            <div key={index} className="flex items-center justify-between py-3 border-b border-slate-100">
                                <div className="flex items-center space-x-3">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                    <span className="text-sm text-slate-600">
                                        {new Date(item.date).toLocaleDateString()} - Payment received
                                    </span>
                                </div>
                                <span className="text-sm font-medium text-slate-800">
                                    +{formatCurrency(item.amount)}
                                </span>
                            </div>
                        ))}

                        {data.recentActivity.length > 3 && (
                            <div className="text-center mt-4">
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="text-sm text-blue-600 underline hover:text-blue-800"
                                >
                                    View All
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal for All Recent Activity */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg w-full max-w-lg p-6 shadow-xl relative max-h-[80vh] overflow-y-auto">
                            <button
                                onClick={() => setShowModal(false)}
                                className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <h2 className="text-lg font-semibold text-slate-800 mb-4">All Recent Activity</h2>
                            <div className="space-y-3">
                                {data.recentActivity.map((item, index) => (
                                    <div key={index} className="flex justify-between border-b py-2">
                                        <span className="text-sm text-slate-600">
                                            {new Date(item.date).toLocaleDateString()}
                                        </span>
                                        <span className="text-sm font-medium text-slate-800">
                                            {formatCurrency(item.amount)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}
